import {css} from '@emotion/css';
import Assert from 'Common/Assert';
import {Show, createSignal} from 'solid-js';
import {Banner, utilityPageStyle} from 'Shared/users/UtilityPageTheme';
import { SiteType } from 'Common/SiteType';
import { useServer, useSite } from 'Shared/SiteProvider';
import { ConnectionToServer } from 'Shared/Root';

const formStyle = css({
	form: {	
		gridTemplateColumns: '100%'
	},

	button: {
		width: '100%',
		marginBottom: '1em'
	},

	input: {
		width: '100%'
	},

	'.forgotPasswordLink': {
		textAlign: 'right',
		color: '#888'
	},

	h2: {
		color: '#222'
	}
});

const labelStyle = css({
	margin: '6px 0',
	fontWeight: 'bold',
	fontSize: 12,
	whiteSpace: 'nowrap',
	color: '#666',
	fontFamily: 'inherit',
	cursor: 'pointer',
	padding: 0  //TODO remove/look at again after removing Less
});

const submitButtonStyle = css({
	borderRadius: '0.4em',
	padding: '0.44em 2.25em',
	backgroundColor: '#229b22',
	color: 'white',
	borderWidth: 0,
	boxShadow: 'none',
	marginTop: '1em',
	whiteSpace: 'nowrap',
	fontSize: 'inherit',

	'&:focus': {
		outline: `1px dotted white`,
		outlineOffset: -4
	},

	'&:hover': {
		filter: 'brightness(95%)'
	}
});

export const inputStyle = css({
	width: '100%',
	font: '15px Arial, Helvetica, sans-serif',
	borderTop: 'none',
	borderRight: 'none',
	borderLeft: 'none',
	borderImage: 'initial',
	borderBottom: '1px solid rgb(239, 239, 239)',
	padding: '5px 10px 5px 6px',
	transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
	backgroundColor: 'rgb(232, 240, 254)'
});

const errorStyle = css({
	color: 'red'
});


export function AuthenticationForm()
{
	const loginStyle = css(formStyle,{
		paddingBottom: '2em',
	});

	const verticalStyle = css({
		display: 'flex',
		flexDirection: 'column',
		gap: '1.2em'
	});

	const aStyle = css({
		textDecoration: 'none',
		color: '#005394'
	});

	const forgotStyle = css({
		display: 'flex',
		flexDirection: 'row-reverse'
	});


	const server = useServer();
	const site = useSite();
	const [errors,setErrors] = createSignal({});

	const redirect = () => entryUrl(site.type);

// TODO can I delete authenticators.ts?

	return (
		<section class={loginStyle}>
			<h2>Log in</h2>

			<Show when={site.type=='artist'}>
				<p>
					Please use your artist's official booking email address to log in.
				</p>
			</Show>

			<form class={verticalStyle} onSubmit={e => e.preventDefault()}>	
				<div>
					<label class={labelStyle} for='email'>Email</label>

					<Show when={errors().email}>
						<div class={errorStyle}>{errors().email}</div>
					</Show>

					<input class={inputStyle} id='email' type='email' name='email' value='' placeholder='you@example.org' autocomplete='email' required/>
				</div>

				<div>
					<label class={labelStyle} for='password'>Password</label>

					<Show when={errors().password}>
						<div class={errorStyle}>{errors().password}</div>
					</Show>

					<input class={inputStyle} id='password' type='password' name='password' placeholder='********' autocomplete='current-password' required/>
				</div>

				<div>
					<button class={submitButtonStyle} onClick={() => logIn(server,redirect(),setErrors)}>Log In</button>
					<div class={forgotStyle}>
						<a class={aStyle} href='/forgotPassword'>Forgot?</a>
					</div>
				</div>
			</form>
		</section>
	);
}

export function RegistrationForm(P:{setRegoEmail:(email:string)=>void})
{
	const registrationStyle = css(formStyle,{
		paddingTop: '1em',
		borderTop: 'solid 1px #aaa'
	});


	const server = useServer();
	const [errors,setErrors] = createSignal({});

	return (
		<section class={registrationStyle}>
			<h2>Or register</h2>

			<p>
				Please use your artist's official booking email address to register.
			</p>

			<form onSubmit={e => e.preventDefault()}>
				<div>
					<label class={labelStyle} for='firstName'>First name&#42;</label>

					<Show when={errors().firstName}>
						<div class={errorStyle}>{errors().firstName}</div>
					</Show>

					<input class={inputStyle} id='firstName' type='text' name='firstname' value='' />
				</div>

				<div>
					<label class={labelStyle} for='lastName'>Last name&#42;</label>

					<Show when={errors().lastName}>
						<div class={errorStyle}>{errors().lastName}</div>
					</Show>

					<input class={inputStyle} id='lastName' type='text' name='lastname' value='' />
				</div>

				<div>
					<label class={labelStyle} for='regoEmail'>Email&#42;</label>

					<Show when={errors().regoEmail}>
						<div class={errorStyle}>{errors().regoEmail}</div>
					</Show>

					<input class={inputStyle} id='regoEmail' type='email' name='email' value='' placeholder='you@example.org' autocomplete='email' />
				</div>

				<button class={submitButtonStyle} onClick={() => submitRegistration(server,P.setRegoEmail,setErrors)}>
					Register
				</button>
			</form>
		</section>
	);
}

async function logIn(server:ConnectionToServer,redirect:string,setErrors)
{
	const userName = Assert.htmlInputElement(document.getElementById('email')).value.trim();
	const password = Assert.htmlInputElement(document.getElementById('password')).value.trim();

	try {
		await server.login.mutate({userName:userName,password:password});
		document.location = redirect;
	}
	catch(err) {
		if (err.message == 'email')
			setErrors({email: 'Unknown email address'});
		else if (err.message == 'password')
			setErrors({password: 'Incorrect password'});
		else
			throw err;
	}
}

async function submitRegistration(server:ConnectionToServer,setSentRegoEmail,setErrors)
{
	const firstName = Assert.htmlInputElement(document.getElementById('firstName')).value.trim();
	const lastName = Assert.htmlInputElement(document.getElementById('lastName')).value.trim();
	const userName = Assert.htmlInputElement(document.getElementById('regoEmail')).value.trim();

	const ret = await server.register.mutate({firstName,lastName,userName});

	if (ret.errors)
		setErrors(ret.errors);
	else
		setSentRegoEmail(userName);
}


/* --- Registration sent message: --- */

export function RegoEmailSent(P)
{
	const tickStyle = css({
		color: 'green',
		fontSize: '7em'
	});

	return (
		<div class={utilityPageStyle()}>
			<Banner />

			<div class={tickStyle}>&#10004</div>

			<p>
				A registration email has been sent to {P.email}.  You will need to click on the link
				in it to complete your registration.
			</p>
			<p>
				If you don't see this email in your inbox within 15 minutes, 
				look for it in your junk mail folder. If you find it there, 
				please mark it as &ldquo;Not Junk&rdquo;. 
			</p>
		</div>
	);
}

export function entryUrl(type:SiteType)
{
	switch(type)
	{
		case 'artist':	return '/manager';
		case 'admin':	return '/dashboard';
		default:		return '/admin';
	}
}

export function failedLoginUrl(type:SiteType)
{
	switch(type)
	{
		case 'artist':	return '/';
		default:		return '/login';
	}
}
